import { useState, useEffect, useCallback } from "react";
import authRequest from "@/api/auth-request";
import * as ApiService from "@/services/ApiService";
import { useProfileStore } from "@/store/profile";

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  [key: string]: unknown;
}

export interface ValidatedUserInfo {
  username: string;
  password: string;
  code: string;
  userID: number;
  sysUser: boolean;
  corporate: boolean;
  consultant: boolean;
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
  appStartupID: number;
  facilityID: number;
  facilityKey: string;
  intakeKey?: string;
  userRoleID: number;
  fileVaultPrimary: boolean;
  productID: number;
  isDesktop: boolean;
  patientID: number;
  msg: string;
  success: true;
  token: string;
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [logged, setLogged] = useState(false);

  const setFacilityKey = useProfileStore((s) => s.setFacilityKey);
  const setIntakeKey = useProfileStore((s) => s.setIntakeKey);

  const checkAuth = useCallback((): boolean => {
    const token = sessionStorage.getItem("config");
    const completeAuth = sessionStorage.getItem("completeAuth");

    if (!token || !completeAuth) {
      setIsAuthenticated(false);
      setLogged(false);
      return false;
    }

    setIsAuthenticated(true);
    setLogged(true);
    return true;
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(
    async (
      username: string,
      password: string
    ): Promise<User | undefined> => {
      try {
        const userDataRaw = await authRequest.request.validateUser({
          username,
          password,
        });

        const data = userDataRaw.data as (User & { token?: string | null; success?: boolean; msg?: string });
        if (data?.success === false || !data?.token) {
          const message = data?.msg?.trim() || "Authentication failed.";
          throw new Error(message);
        }
        sessionStorage.setItem("config", data.token);
        setUser(data);
        setLogged(true);
        return data;
      } catch (error) {
        // Don't log the full error: it can include request config with username/password
        console.error("Login error:", error instanceof Error ? error.message : "Login failed");
        throw error;
      }
    },
    []
  );

  const logout = useCallback((): void => {
    sessionStorage.clear();
    setIsAuthenticated(false);
    setUser(null);
    setLogged(false);
  }, []);

  const getToken = useCallback((): string | null => {
    return sessionStorage.getItem("config");
  }, []);

  const completeAuth = useCallback((): void => {
    sessionStorage.setItem("completeAuth", "true");
    setIsAuthenticated(true);
    setLogged(true);
  }, []);

  const getLink = useCallback(async (linkKey: string): Promise<unknown> => {
    const link = await authRequest.request.getLink(linkKey);
    return link.data;
  }, []);

  const validateUser = useCallback(
    async (
      username: string,
      password: string
    ): Promise<ValidatedUserInfo> => {
      try {
        const response = await ApiService.post("/Portal/ValidateUser", {
          username,
          password,
        });

        const data = response.data as ValidatedUserInfo;
        if (response.status === 200 && data?.token) {
          sessionStorage.setItem("config", data.token);
          setFacilityKey(data.facilityKey);
          setIntakeKey(data.intakeKey ?? null);
        }
        return data;
      } catch (error) {
        console.error("Validate user error:", error);
        throw error;
      }
    },
    [setFacilityKey, setIntakeKey]
  );

  return {
    user,
    isAuthenticated,
    logged,
    login,
    logout,
    getToken,
    checkAuth,
    completeAuth,
    getLink,
    validateUser,
  };
}
